import {
  archiveTag,
  createTag,
  finishTimer,
  resumeCompletedTimer,
  renameTag,
  reviseCompletedTimer,
  reviewDecision,
  reviewExplanation,
  startTimer,
  switchTimer,
  type CompletedTimer,
  type Presence,
  type ReviewReason,
  type TagDefinition,
} from "../src/domain";
import { activityModeForTags, dayTagBreakdown, emptyLedger, isQuietHour, migrateLedger, type LocalLedger, type NudgeEvent } from "../src/ledger";
import { resolveTheme, themeColors, type Theme } from "../src/theme";
import { addPasskey, approveDeviceAuthorization, authenticatePasskey, captureEnrollmentToken, completeDeviceAuthorization, enrollPasskey, listPasskeys, loadAuthState, logout, passkeyError, passkeysSupported, removePasskey, startDeviceAuthorization, type PasskeySummary } from "./auth";
import { isNative, loadNativeActivity, loadNativeStatus, nativeNotificationPermission, openDeviceAuthorization, requestInputMonitoring, setCollectionEnabled, syncNativeReminder, type ActivitySample, type NativeStatus } from "./native";
import { enableNotifications, notificationState, sendTestNotification, showReviewNotification, type NotificationState } from "./push";
import { beginInlineTextEdit, createChipAction, createDataChip, createInlineCreator, enhanceControls, hydrateIcons, icon, setIcon, syncEnhancedControls } from "./ui";
import { openWheelPicker, type WheelPickerColumn } from "./wheel-picker";
import { HttpSyncTransport, LedgerSyncClient, type SyncStatus } from "./sync";

interface PresenceDetail { state: Presence; app?: string }
type Page = "now" | "summary" | "profile";

const storageKey = "time-tile.ledger.v2";
const legacyStorageKey = "time-tile.ledger.v1";
const themeStorageKey = "time-tile.theme.v1";
const enrollmentToken = captureEnrollmentToken();
const deviceApprovalPage = location.pathname === "/device-auth";
const deviceApprovalRequestId = new URLSearchParams(location.search).get("request");
const element = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
let ledger = emptyLedger();
let presence: Presence = "unknown";
let previousPresence: Presence = "unknown";
let awayStartedAt: number | undefined;
let engagedStartedAt: number | undefined;
let nativeStatus: NativeStatus = { native: false, collecting: false, inputMonitoring: "unknown", presence: "unknown" };
let tick: number | undefined;
let nativeTick: number | undefined;
let syncTick: number | undefined;
let toastTimer: number | undefined;
let activeTheme: Theme = "light";
let startTagIds = new Set<string>();
let switchTagIds = new Set<string>();
let summaryTagId: string | null = null;
let openTimelineTagEntryId: string | null = null;
let presentedPromptKey: string | null = null;
let webNotificationKey: string | null = null;
let syncClient: LedgerSyncClient | undefined;

const idleView = element<HTMLElement>("idle-view");
const runningView = element<HTMLElement>("running-view");
const runningSummary = element<HTMLElement>("running-summary");
const switchPanel = element<HTMLElement>("switch-panel");
const reviewCard = element<HTMLElement>("review-card");
const form = element<HTMLFormElement>("start-form");
const input = element<HTMLInputElement>("task-input");
const switchForm = element<HTMLFormElement>("switch-form");
const switchInput = element<HTMLInputElement>("switch-input");
const timeline = element<HTMLOListElement>("timeline");
const dayRivers = element<HTMLOListElement>("day-rivers");
const authView = element<HTMLElement>("auth-view");
const appShell = element<HTMLElement>("app-shell");
const bottomNav = element<HTMLElement>("bottom-nav");
const authButton = element<HTMLButtonElement>("auth-button");
const authCancelButton = element<HTMLButtonElement>("auth-cancel-button");
const authLabelWrap = element<HTMLElement>("auth-label-wrap");
const authLabelInput = element<HTMLInputElement>("auth-label-input");
const passkeyForm = element<HTMLFormElement>("passkey-form");
const passkeyLabel = element<HTMLInputElement>("passkey-label");
const passkeyAddButton = element<HTMLButtonElement>("passkey-add-button");
const passkeyList = element<HTMLTableSectionElement>("passkey-list");
const passkeyStatus = element<HTMLElement>("passkey-status");

function applyTheme(theme: Theme, persist = false): void {
  activeTheme = theme;
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
  document.querySelector<HTMLMetaElement>("#theme-color")?.setAttribute("content", themeColors[theme]);
  document.querySelectorAll<HTMLButtonElement>("[data-theme-choice]").forEach((button) => button.setAttribute("aria-pressed", String(button.dataset.themeChoice === theme)));
  const next = theme === "light" ? "dark" : "light";
  document.querySelectorAll<HTMLButtonElement>(".theme-toggle").forEach((button) => { button.setAttribute("aria-label", `Use ${next} mode`); button.title = `Use ${next} mode`; });
  const railLabel = document.getElementById("rail-theme-label");
  if (railLabel) railLabel.textContent = `${next[0]!.toUpperCase()}${next.slice(1)} mode`;
  document.querySelectorAll<HTMLElement>("[data-theme-icon]").forEach((container) => setIcon(container, next === "dark" ? "moon" : "sun"));
  if (persist) localStorage.setItem(themeStorageKey, theme);
}

function initializeTheme(): void {
  const media = matchMedia("(prefers-color-scheme: dark)");
  applyTheme(resolveTheme(localStorage.getItem(themeStorageKey), media.matches));
  media.addEventListener("change", (event) => { if (!localStorage.getItem(themeStorageKey)) applyTheme(event.matches ? "dark" : "light"); });
}

function loadLedger(): LocalLedger {
  const raw = localStorage.getItem(storageKey) ?? localStorage.getItem(legacyStorageKey);
  if (!raw) return emptyLedger();
  try {
    const migrated = migrateLedger(JSON.parse(raw));
    localStorage.setItem(storageKey, JSON.stringify(migrated));
    return migrated;
  } catch {
    localStorage.setItem(`${storageKey}.corrupt.${Date.now()}`, raw);
    return emptyLedger();
  }
}

function persistLedger(): void {
  localStorage.setItem(storageKey, JSON.stringify(ledger));
}

function saveLedger(): void {
  persistLedger();
  syncClient?.commit(ledger);
  void syncReminderProjection();
}

function renderSyncStatus(status: SyncStatus): void {
  const container = element<HTMLElement>("sync-state");
  const labels: Record<SyncStatus["phase"], string> = {
    synced: "Synced",
    syncing: "Syncing",
    local: "On this device",
    conflict: "Review sync",
    error: "Sync error",
  };
  container.textContent = labels[status.phase];
  container.dataset.state = status.phase === "synced" ? "ready" : status.phase === "syncing" || status.phase === "local" ? "available" : "error";
  container.title = status.detail;
  if (status.phase === "conflict") showToast("Another device changed the timer. Its newer version is shown.");
}

function applySyncedLedger(next: LocalLedger): void {
  ledger = next;
  persistLedger();
  renderNow();
  const page = routeForPath();
  if (page === "summary") void renderSummary();
  if (page === "profile") { renderTagSettings(); renderNudgeSettings(); }
}

function formatDuration(milliseconds: number, seconds = true): string {
  const total = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(total / 3600).toString().padStart(2, "0");
  const minutes = Math.floor((total % 3600) / 60).toString().padStart(2, "0");
  const remainder = (total % 60).toString().padStart(2, "0");
  return seconds ? `${hours}:${minutes}:${remainder}` : `${Number(hours)}h ${minutes}m`;
}

const clockFormatter = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
const clockWithSecondsFormatter = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit", second: "2-digit" });

function formatClock(timestamp: number): string { return clockFormatter.format(timestamp); }

function formatClockWithSeconds(timestamp: number): string { return clockWithSecondsFormatter.format(timestamp); }

interface DayRiver {
  key: string;
  label: string;
  color: string;
  milliseconds: number;
  live: boolean;
}

function updateDaySoFar(now = Date.now()): void {
  const dayStart = new Date(now);
  dayStart.setHours(0, 0, 0, 0);
  const breakdown = dayTagBreakdown(ledger, dayStart.getTime(), now);
  const rows: DayRiver[] = breakdown.tags.map((entry) => {
    if (entry.tagId === null) return { key: "untagged", label: "Untagged", color: "var(--faint)", milliseconds: entry.milliseconds, live: entry.live };
    const tag = ledger.tags.find((candidate) => candidate.id === entry.tagId);
    return { key: entry.tagId, label: tag?.label ?? "Unknown tag", color: tag?.color ?? "var(--faint)", milliseconds: entry.milliseconds, live: entry.live };
  });
  const clock = element<HTMLTimeElement>("day-clock");
  clock.dateTime = new Date(now).toISOString();
  clock.textContent = formatClockWithSeconds(now);
  element("day-total").textContent = formatDuration(breakdown.totalMilliseconds);

  const signature = rows.map((row) => `${row.key}:${row.label}:${row.color}:${row.live}`).join("|") || "empty";
  if (dayRivers.dataset.signature !== signature) {
    dayRivers.dataset.signature = signature;
    if (!rows.length) {
      const empty = document.createElement("li");
      empty.className = "day-empty";
      empty.textContent = "Your day will draw itself here.";
      dayRivers.replaceChildren(empty);
    } else {
      dayRivers.replaceChildren(...rows.map((row) => {
        const item = document.createElement("li");
        item.className = "day-river";
        item.dataset.live = String(row.live);
        const label = document.createElement("div");
        label.className = "day-river-label";
        const dot = document.createElement("span");
        dot.className = "day-river-dot";
        const name = document.createElement("span");
        name.textContent = row.label;
        label.append(dot, name);
        if (row.live) {
          const live = document.createElement("span");
          live.className = "day-river-live";
          live.textContent = "Live";
          label.append(live);
        }
        const track = document.createElement("div");
        track.className = "day-river-track";
        track.setAttribute("aria-hidden", "true");
        const bar = document.createElement("div");
        bar.className = "day-river-bar";
        track.append(bar);
        const value = document.createElement("span");
        value.className = "day-river-value";
        item.append(label, track, value);
        return item;
      }));
    }
  }

  [...dayRivers.querySelectorAll<HTMLElement>(".day-river")].forEach((item, index) => {
    const row = rows[index];
    if (!row) return;
    item.style.setProperty("--tag-color", row.color);
    item.style.setProperty("--day-width", `${breakdown.totalMilliseconds ? Math.min(100, row.milliseconds / breakdown.totalMilliseconds * 100) : 0}%`);
    item.querySelector<HTMLElement>(".day-river-value")!.textContent = formatDuration(row.milliseconds);
    item.setAttribute("aria-label", `${row.label}, ${formatDuration(row.milliseconds)}${row.live ? ", currently running" : ""}`);
  });
  const details = rows.length ? rows.map((row) => `${row.label} ${formatDuration(row.milliseconds)}`).join(", ") : "no confirmed time yet";
  element("day-so-far-summary").textContent = `As of ${formatClockWithSeconds(now)}, ${formatDuration(breakdown.totalMilliseconds)} confirmed. ${details}.`;
}

function showToast(message: string): void {
  const toast = element<HTMLElement>("toast");
  toast.textContent = message;
  toast.classList.add("visible");
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => toast.classList.remove("visible"), 2600);
}

function activeTags(): TagDefinition[] {
  return ledger.tags.filter((tag) => !tag.archivedAt).sort((a, b) => Number(b.isFavorite) - Number(a.isFavorite) || a.label.localeCompare(b.label));
}

const inlineTagColors = ["#5b4bff", "#e55c91", "#ef8d3c", "#2f9b74", "#3f8bd8", "#a35cf0"];

interface TagChipRenderOptions {
  interactive?: boolean;
  includeSelectedArchived?: boolean;
  onSelectionChange?: (tagIds: string[], changedTagId: string) => void;
  persistCreatedTag?: boolean;
  persistSelection?: boolean;
}

function createAndSelectInlineTag(label: string, selected: Set<string>, container: HTMLElement, options: TagChipRenderOptions): void {
  if (ledger.tags.some((candidate) => !candidate.archivedAt && candidate.label.toLowerCase() === label.trim().toLowerCase())) {
    throw new Error("That tag already exists.");
  }
  const color = inlineTagColors[ledger.tags.length % inlineTagColors.length]!;
  const tag = createTag(label, color, "mixed");
  ledger.tags.push(tag);
  selected.add(tag.id);
  options.onSelectionChange?.([...selected], tag.id);
  if (options.persistCreatedTag !== false) saveLedger();
  renderTagChips(container, selected, options);
  focusTagChip(container, tag.id);
  showToast(`${tag.label} created and selected.`);
}

function focusTagChip(container: HTMLElement, tagId: string): void {
  [...container.querySelectorAll<HTMLButtonElement>("[data-tag-id]")]
    .find((button) => button.dataset.tagId === tagId)
    ?.focus();
}

function renderTagChips(container: HTMLElement, selected: Set<string>, options: TagChipRenderOptions = {}): void {
  const interactive = options.interactive ?? true;
  const tags = interactive
    ? ledger.tags
      .filter((tag) => !tag.archivedAt || Boolean(options.includeSelectedArchived && selected.has(tag.id)))
      .sort((a, b) => Number(b.isFavorite) - Number(a.isFavorite) || a.label.localeCompare(b.label))
    : ledger.tags.filter((tag) => selected.has(tag.id));
  container.replaceChildren(...tags.map((tag) => {
    const chip = createDataChip({
      label: tag.label,
      color: tag.color,
      selected: selected.has(tag.id),
      interactive,
      onToggle: interactive ? () => {
        if (selected.has(tag.id)) selected.delete(tag.id); else selected.add(tag.id);
        options.onSelectionChange?.([...selected], tag.id);
        if (options.onSelectionChange && options.persistSelection !== false) saveLedger();
        renderTagChips(container, selected, options);
        focusTagChip(container, tag.id);
      } : undefined,
    });
    chip.dataset.tagId = tag.id;
    return chip;
  }));
  if (interactive) {
    container.append(createInlineCreator({
      actionText: "New tag",
      actionLabel: "Create a new tag here",
      inputLabel: "New tag name",
      submitLabel: "Create and select tag",
      placeholder: "Tag name",
      emptyError: "Name the tag first.",
      maxLength: 40,
      onCommit: (label) => createAndSelectInlineTag(label, selected, container, options),
    }).element);
  }
}

function renderEntryTags(container: HTMLElement, tagIds: string[]): void {
  const tags = tagIds.flatMap((id) => {
    const tag = ledger.tags.find((candidate) => candidate.id === id);
    return tag ? [tag] : [];
  });
  renderTagChips(container, new Set(tags.map((tag) => tag.id)), { interactive: false });
  container.hidden = tags.length === 0;
}

function recordPrompt(reason: ReviewReason, presentation: NudgeEvent["presentation"]): void {
  if (!ledger.running) return;
  const key = `${ledger.running.id}:${ledger.running.lastReviewedAt ?? ledger.running.startedAt}:${reason}:${presentation}`;
  if (ledger.nudgeEvents.some((event) => event.id === key)) return;
  ledger.nudgeEvents.push({ id: key, timerId: ledger.running.id, reason, presentation, occurredAt: Date.now() });
  ledger.nudgeEvents = ledger.nudgeEvents.slice(-500);
  saveLedger();
}

function resolvePrompt(outcome: NonNullable<NudgeEvent["outcome"]>): void {
  if (!ledger.running) return;
  const event = [...ledger.nudgeEvents].reverse().find((candidate) => candidate.timerId === ledger.running!.id && !candidate.outcome);
  if (event) event.outcome = outcome;
}

function complete(at = Date.now()): void {
  if (!ledger.running) return;
  resolvePrompt("finished");
  ledger.completed.push(finishTimer(ledger.running, at));
  ledger.running = null;
  startTagIds.clear();
  presentedPromptKey = null;
  webNotificationKey = null;
  saveLedger();
  renderNow();
  input.focus();
  showToast("Time saved locally.");
}

function begin(description: string, tagIds: string[], at = Date.now()): void {
  ledger.running = startTimer(description, at, { tagIds, activityMode: activityModeForTags(tagIds, ledger.tags) });
  startTagIds.clear();
  presentedPromptKey = null;
  webNotificationKey = null;
  saveLedger();
  renderNow();
}

function openSwitch(): void {
  if (!ledger.running) return;
  switchTagIds = new Set();
  element("switch-from").textContent = ledger.running.description;
  renderTagChips(element("switch-tags"), switchTagIds);
  runningSummary.hidden = true;
  reviewCard.hidden = true;
  switchPanel.hidden = false;
  switchInput.value = "";
  switchInput.focus();
}

function closeSwitch(): void {
  switchPanel.hidden = true;
  runningSummary.hidden = false;
  switchInput.value = "";
  updateRunning();
  element<HTMLButtonElement>("switch-button").focus();
}

function submitSwitch(description: string, at = Date.now()): void {
  if (!ledger.running) return;
  resolvePrompt("switched");
  const tagIds = [...switchTagIds];
  const result = switchTimer(ledger.running, description, at, { tagIds, activityMode: activityModeForTags(tagIds, ledger.tags) });
  ledger.completed.push(result.completed);
  ledger.running = result.running;
  presentedPromptKey = null;
  webNotificationKey = null;
  saveLedger();
  switchPanel.hidden = true;
  runningSummary.hidden = false;
  renderNow();
  showToast("Switched with no gap.");
}

function scrollToLiveTimer(): void {
  window.scrollTo({
    top: 0,
    behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth",
  });
}

function resumeCompletedEntry(entry: CompletedTimer, at = Date.now()): void {
  try {
    const result = resumeCompletedTimer(entry, ledger.running, at);
    if (!result.resumed) {
      showToast(`Already tracking “${entry.description}”.`);
      scrollToLiveTimer();
      return;
    }
    if (result.completed) {
      resolvePrompt("switched");
      ledger.completed.push(result.completed);
    }
    ledger.running = result.running;
    startTagIds.clear();
    presentedPromptKey = null;
    webNotificationKey = null;
    saveLedger();
    renderNow();
    scrollToLiveTimer();
    showToast(result.completed ? `Switched back to “${entry.description}”.` : `Resumed “${entry.description}”.`);
  } catch (error) {
    showToast(error instanceof Error ? error.message : "Could not resume this entry.");
  }
}

function reviewContext() {
  return {
    presence,
    pageVisible: !document.hidden && routeForPath() === "now" && switchPanel.hidden === true,
    previousPresence,
    returnedAfterAwayMs: awayStartedAt ? Date.now() - awayStartedAt : 0,
    engagedForMs: engagedStartedAt ? Date.now() - engagedStartedAt : 0,
    quietHours: isQuietHour(ledger.nudgePolicy),
  };
}

async function syncReminderProjection(): Promise<void> {
  if (!ledger.running || !ledger.nudgePolicy.enabled) return syncNativeReminder(null).catch(() => undefined);
  const baseline = Math.max(ledger.running.startedAt, ledger.running.lastReviewedAt ?? 0);
  const nextEligibleAtMs = Math.max(baseline + ledger.nudgePolicy.reviewAfterMinutes * 60_000, ledger.running.snoozedUntil ?? 0);
  const clockMinutes = (clock: string): number => { const [hour = 0, minute = 0] = clock.split(":").map(Number); return hour * 60 + minute; };
  return syncNativeReminder({
    timerId: ledger.running.id,
    description: ledger.nudgePolicy.includeTaskName ? ledger.running.description : "",
    startedAtMs: ledger.running.startedAt,
    nextEligibleAtMs,
    activityMode: ledger.running.activityMode,
    includeTaskName: ledger.nudgePolicy.includeTaskName,
    quietStartMinutes: clockMinutes(ledger.nudgePolicy.quietStart),
    quietEndMinutes: clockMinutes(ledger.nudgePolicy.quietEnd),
    timezoneOffsetMinutes: -new Date().getTimezoneOffset(),
  }).catch(() => undefined);
}

function showInlineReview(reason: ReviewReason): void {
  if (!ledger.running || !switchPanel.hidden) return;
  const key = `${ledger.running.id}:${ledger.running.lastReviewedAt ?? ledger.running.startedAt}:${reason}`;
  runningSummary.hidden = true;
  reviewCard.hidden = false;
  element("review-title").textContent = `Still working on “${ledger.running.description}”?`;
  element("review-explanation").textContent = reviewExplanation(reason, Date.now() - ledger.running.startedAt, awayStartedAt ? Date.now() - awayStartedAt : 0);
  if (presentedPromptKey !== key) {
    presentedPromptKey = key;
    recordPrompt(reason, "inline");
  }
}

function updateRunning(): void {
  if (!ledger.running) return;
  element("elapsed").textContent = formatDuration(Date.now() - ledger.running.startedAt);
  if (!ledger.nudgePolicy.enabled) {
    element("review-note").hidden = true;
    reviewCard.hidden = true;
    if (switchPanel.hidden) runningSummary.hidden = false;
    return;
  }
  const decision = reviewDecision(ledger.running, reviewContext(), Date.now(), ledger.nudgePolicy.reviewAfterMinutes * 60_000);
  element("review-note").hidden = decision.kind !== "quiet";
  if (decision.kind === "prompt" && !document.hidden) showInlineReview(decision.reason);
  if (decision.kind === "prompt" && document.hidden) {
    const key = `${ledger.running.id}:${decision.dueAt}:${decision.reason}`;
    if (webNotificationKey !== key) {
      webNotificationKey = key;
      void showReviewNotification(ledger.nudgePolicy.includeTaskName ? `Are you still working on “${ledger.running.description}”?` : "Is your current timer still right?")
        .then((shown) => { if (shown) recordPrompt(decision.reason, "web"); })
        .catch(() => undefined);
    }
  }
}

type TimelineEntryPatch = Partial<Pick<CompletedTimer, "description" | "startedAt" | "endedAt" | "tagIds">>;

function timelineEntry(entryId: string): CompletedTimer {
  const entry = ledger.completed.find((candidate) => candidate.id === entryId);
  if (!entry) throw new Error("This entry is no longer available.");
  return entry;
}

function reviseTimelineEntry(entryId: string, patch: TimelineEntryPatch, successMessage: string): boolean {
  const entry = timelineEntry(entryId);
  const revised = reviseCompletedTimer(entry, {
    description: patch.description ?? entry.description,
    startedAt: patch.startedAt ?? entry.startedAt,
    endedAt: patch.endedAt ?? entry.endedAt,
    tagIds: patch.tagIds ?? entry.tagIds,
  });
  const changed = revised !== entry;
  if (changed) {
    const index = ledger.completed.findIndex((candidate) => candidate.id === entryId);
    ledger.completed[index] = revised;
    saveLedger();
  }
  renderTimeline();
  updateDaySoFar();
  if (changed) showToast(successMessage);
  return changed;
}

function focusTimelineControl(entryId: string, selector: string): void {
  requestAnimationFrame(() => {
    const row = [...timeline.querySelectorAll<HTMLElement>("[data-entry-id]")]
      .find((candidate) => candidate.dataset.entryId === entryId);
    row?.querySelector<HTMLElement>(selector)?.focus();
  });
}

function beginTimelineTitleEdit(button: HTMLButtonElement, entryId: string): void {
  const entry = timelineEntry(entryId);
  beginInlineTextEdit({
    trigger: button,
    value: entry.description,
    label: `Edit title: ${entry.description}`,
    inputClassName: "timeline-title-input",
    maxLength: 180,
    onCommit: (value) => { reviseTimelineEntry(entryId, { description: value }, "Title updated."); },
    onError: (error) => showToast(error instanceof Error ? error.message : "Could not update the title."),
    onAfterClose: () => focusTimelineControl(entryId, ".timeline-title-action"),
  });
}

function renderTimelineTagTray(container: HTMLElement, entry: CompletedTimer): void {
  const tray = document.createElement("section");
  tray.className = "timeline-tag-tray";
  tray.id = `timeline-tag-tray-${entry.id}`;
  tray.setAttribute("aria-label", `Tags for ${entry.description}`);
  const header = document.createElement("div");
  header.className = "timeline-tag-tray-header";
  const hint = document.createElement("span");
  hint.textContent = "Choose again to remove";
  const done = document.createElement("button");
  done.type = "button";
  done.className = "ui-button ui-button-quiet ui-button-small";
  done.textContent = "Done";
  done.addEventListener("click", () => {
    openTimelineTagEntryId = null;
    renderTimeline();
    focusTimelineControl(entry.id, ".timeline-tag-action");
  });
  header.append(hint, done);
  const picker = document.createElement("div");
  picker.className = "timeline-tag-picker";
  const selectedTagIds = new Set(entry.tagIds);
  renderTagChips(picker, selectedTagIds, {
    includeSelectedArchived: true,
    persistCreatedTag: false,
    persistSelection: false,
    onSelectionChange: (tagIds, changedTagId) => {
      reviseTimelineEntry(entry.id, { tagIds }, "Tags updated.");
      requestAnimationFrame(() => focusTagChip(timeline, changedTagId));
    },
  });
  tray.append(header, picker);
  container.append(tray);
}

const numericRange = (maximum: number, start = 0): number[] => Array.from({ length: maximum - start + 1 }, (_, index) => index + start);
const twoDigits = (value: number): string => String(value).padStart(2, "0");

function openStartTimePicker(entryId: string, trigger: HTMLElement): void {
  const entry = timelineEntry(entryId);
  const value = new Date(entry.startedAt);
  const twelveHour = clockFormatter.resolvedOptions().hour12 !== false;
  const columns: WheelPickerColumn[] = twelveHour
    ? [
      { id: "hour", label: "Hour", values: numericRange(12, 1), value: value.getHours() % 12 || 12 },
      { id: "minute", label: "Minute", values: numericRange(59), value: value.getMinutes(), format: twoDigits },
      { id: "period", label: "Period", values: [0, 1], value: value.getHours() >= 12 ? 1 : 0, format: (period) => period ? "PM" : "AM" },
    ]
    : [
      { id: "hour", label: "Hour", values: numericRange(23), value: value.getHours(), format: twoDigits },
      { id: "minute", label: "Minute", values: numericRange(59), value: value.getMinutes(), format: twoDigits },
    ];
  openWheelPicker({
    title: "Start time",
    trigger,
    columns,
    onConfirm: (selection) => {
      const current = timelineEntry(entryId);
      const next = new Date(current.startedAt);
      const chosenHour = twelveHour
        ? (selection.hour! % 12) + (selection.period ? 12 : 0)
        : selection.hour!;
      if (chosenHour === next.getHours() && selection.minute === next.getMinutes()) return;
      next.setHours(chosenHour, selection.minute!, next.getSeconds(), next.getMilliseconds());
      reviseTimelineEntry(entryId, { startedAt: next.getTime() }, "Start time updated.");
      focusTimelineControl(entryId, ".timeline-time-action");
    },
  });
}

function openDurationPicker(entryId: string, trigger: HTMLElement): void {
  const entry = timelineEntry(entryId);
  const totalSeconds = Math.max(0, Math.floor((entry.endedAt - entry.startedAt) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const hourValues = numericRange(Math.min(Math.max(99, hours), 999));
  if (hours > 999) hourValues.push(hours);
  const columns: WheelPickerColumn[] = [
    { id: "hours", label: "Hours", values: hourValues, value: hours, format: twoDigits },
    { id: "minutes", label: "Min", values: numericRange(59), value: Math.floor(totalSeconds / 60) % 60, format: twoDigits },
    { id: "seconds", label: "Sec", values: numericRange(59), value: totalSeconds % 60, format: twoDigits },
  ];
  openWheelPicker({
    title: "Duration",
    trigger,
    columns,
    onConfirm: (selection) => {
      const current = timelineEntry(entryId);
      const currentSeconds = Math.floor((current.endedAt - current.startedAt) / 1000);
      const nextSeconds = selection.hours! * 3600 + selection.minutes! * 60 + selection.seconds!;
      if (nextSeconds === currentSeconds) return;
      reviseTimelineEntry(entryId, { endedAt: current.startedAt + nextSeconds * 1000 }, "Duration updated.");
      focusTimelineControl(entryId, ".timeline-duration-action");
    },
  });
}

function renderTimeline(): void {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const entries = ledger.completed.filter((entry) => entry.endedAt >= today.getTime()).sort((a, b) => b.startedAt - a.startedAt);
  if (openTimelineTagEntryId && !entries.some((entry) => entry.id === openTimelineTagEntryId)) openTimelineTagEntryId = null;
  timeline.replaceChildren(...entries.map((entry) => {
    const item = document.createElement("li");
    item.dataset.entryId = entry.id;
    const tagTrayOpen = openTimelineTagEntryId === entry.id;
    if (tagTrayOpen) item.dataset.tagTrayOpen = "true";
    const timeAction = document.createElement("button");
    timeAction.type = "button";
    timeAction.className = "timeline-time-action";
    timeAction.setAttribute("aria-label", `Edit start time, currently ${formatClock(entry.startedAt)}`);
    const time = document.createElement("time");
    time.dateTime = new Date(entry.startedAt).toISOString();
    time.textContent = formatClock(entry.startedAt);
    timeAction.append(time);
    timeAction.addEventListener("click", () => openStartTimePicker(entry.id, timeAction));
    const copy = document.createElement("div");
    copy.className = "timeline-copy";
    const description = document.createElement("button");
    description.type = "button";
    description.className = "timeline-title-action";
    description.textContent = entry.description;
    description.setAttribute("aria-label", `Edit title: ${entry.description}`);
    description.addEventListener("click", () => beginTimelineTitleEdit(description, entry.id));
    const tags = document.createElement("div");
    tags.className = "entry-tags";
    renderEntryTags(tags, entry.tagIds);
    const tagSummary = document.createElement("div");
    tagSummary.className = "timeline-tag-summary";
    const tagTrayId = `timeline-tag-tray-${entry.id}`;
    const tagAction = createChipAction({
      icon: "plus",
      text: "Tag",
      label: `Add or remove tags from ${entry.description}`,
      expanded: tagTrayOpen,
      controls: tagTrayOpen ? tagTrayId : undefined,
      onActivate: () => {
        openTimelineTagEntryId = tagTrayOpen ? null : entry.id;
        renderTimeline();
        if (!tagTrayOpen) focusTimelineControl(entry.id, ".timeline-tag-picker [data-tag-id], .timeline-tag-picker .ui-chip-action");
      },
    });
    tagAction.classList.add("timeline-tag-action");
    const actions = document.createElement("div");
    actions.className = "timeline-row-actions";
    const resumeAction = document.createElement("button");
    resumeAction.type = "button";
    resumeAction.className = "ui-button ui-button-secondary ui-button-small timeline-resume-action";
    resumeAction.dataset.timelineResumeAction = entry.id;
    resumeAction.setAttribute("aria-label", `Resume ${entry.description}`);
    resumeAction.append(icon("play"), document.createTextNode("Resume"));
    resumeAction.addEventListener("click", () => resumeCompletedEntry(entry));
    actions.append(resumeAction);
    tagSummary.append(tags, tagAction, actions);
    copy.append(description, tagSummary);
    if (tagTrayOpen) renderTimelineTagTray(copy, entry);
    const duration = document.createElement("button");
    duration.type = "button";
    duration.className = "duration timeline-duration-action";
    duration.textContent = formatDuration(entry.endedAt - entry.startedAt).replace(/^00:/, "");
    duration.setAttribute("aria-label", `Edit duration, currently ${formatDuration(entry.endedAt - entry.startedAt)}`);
    duration.addEventListener("click", () => openDurationPicker(entry.id, duration));
    item.append(timeAction, copy, duration);
    item.addEventListener("dblclick", (event) => {
      if (
        tagTrayOpen || !matchMedia("(hover: hover) and (pointer: fine)").matches ||
        !(event.target instanceof Element) || event.target.closest("button, a, input, select, textarea, form")
      ) return;
      resumeCompletedEntry(entry);
    });
    return item;
  }));
  const total = entries.reduce((sum, entry) => sum + entry.endedAt - entry.startedAt, 0);
  element("today-total").textContent = entries.length ? `${formatDuration(total)} recorded` : "Nothing recorded yet";
  const latest = entries[0];
  element("last-finished").textContent = latest ? `Last: ${latest.description} · ${formatClock(latest.endedAt)}` : "Press Return to begin.";
}

function renderNow(): void {
  const running = Boolean(ledger.running);
  idleView.hidden = running;
  runningView.hidden = !running;
  switchPanel.hidden = true;
  reviewCard.hidden = true;
  runningSummary.hidden = false;
  window.clearInterval(tick);
  renderTagChips(element("start-tags"), startTagIds);
  if (ledger.running) {
    element("running-title").textContent = ledger.running.description;
    renderEntryTags(element("running-tags"), ledger.running.tagIds);
  }
  const updateLiveNow = (): void => {
    if (ledger.running) updateRunning();
    updateDaySoFar();
  };
  updateLiveNow();
  tick = window.setInterval(updateLiveNow, 1000);
  renderTimeline();
  void syncReminderProjection();
}

function updatePresence(detail: PresenceDetail): void {
  const now = Date.now();
  previousPresence = presence;
  presence = detail.state;
  if (presence === "away" && previousPresence !== "away") awayStartedAt = now;
  if (presence === "engaged" && previousPresence !== "engaged") engagedStartedAt = now;
  if (presence !== "engaged") engagedStartedAt = undefined;
  const container = element<HTMLElement>("presence");
  container.dataset.state = presence;
  const labels: Record<Presence, string> = { engaged: "Active", attending: "Attending", away: "Away", unknown: "Local" };
  element("presence-label").textContent = detail.app ? `${labels[presence]} · ${detail.app}` : labels[presence];
  updateRunning();
}

function routeForPath(pathname = location.pathname): Page {
  if (pathname.startsWith("/summary")) return "summary";
  if (pathname.startsWith("/profile")) return "profile";
  return "now";
}

async function showPage(page: Page, push = false): Promise<void> {
  if (push) history.pushState(null, "", page === "now" ? "/" : `/${page}`);
  document.querySelectorAll<HTMLElement>("[data-page]").forEach((section) => { section.hidden = section.dataset.page !== page; });
  document.querySelectorAll<HTMLAnchorElement>("[data-route]").forEach((link) => { if (link.dataset.route === page) link.setAttribute("aria-current", "page"); else link.removeAttribute("aria-current"); });
  document.title = page === "now" ? "Time Tile" : `${page[0]!.toUpperCase()}${page.slice(1)} · Time Tile`;
  if (page === "now") updateRunning();
  if (page === "summary") await renderSummary();
  if (page === "profile") { renderTagSettings(); renderNudgeSettings(); await Promise.all([refreshNotifications(), refreshNativeStatus(), refreshPasskeys()]); }
  window.scrollTo({ top: 0, behavior: "instant" });
}

function startOfDay(value: Date): number {
  const day = new Date(value); day.setHours(0, 0, 0, 0); return day.getTime();
}

function clippedDuration(start: number, end: number, dayStart: number): number {
  return Math.max(0, Math.min(end, dayStart + 86_400_000) - Math.max(start, dayStart));
}

function renderSummaryFilters(): void {
  const container = element("summary-tags");
  const all = document.createElement("button");
  all.type = "button"; all.className = "ui-chip ui-chip-filter"; all.textContent = "All"; all.setAttribute("aria-pressed", String(summaryTagId === null));
  all.addEventListener("click", () => { summaryTagId = null; void renderSummary(); });
  container.replaceChildren(all, ...activeTags().map((tag) => createDataChip({
    label: tag.label,
    color: tag.color,
    selected: summaryTagId === tag.id,
    onToggle: () => { summaryTagId = tag.id; void renderSummary(); },
  })));
}

async function renderSummary(): Promise<void> {
  renderSummaryFilters();
  let observed: ActivitySample[] = [];
  try { observed = await loadNativeActivity(7); } catch { /* browser and unavailable-native states are expected */ }
  const week = element<HTMLElement>("week");
  const now = new Date();
  const days = Array.from({ length: 7 }, (_, index) => { const date = new Date(now); date.setDate(now.getDate() - (6 - index)); return startOfDay(date); });
  week.replaceChildren(...days.map((dayStart) => {
    const dayEnd = dayStart + 86_400_000;
    const confirmed = ledger.completed.filter((entry) => entry.endedAt > dayStart && entry.startedAt < dayEnd && (!summaryTagId || entry.tagIds.includes(summaryTagId)));
    const samples = observed.filter((entry) => entry.bucketEndMs > dayStart && entry.bucketStartMs < dayEnd);
    const confirmedMs = confirmed.reduce((sum, entry) => sum + clippedDuration(entry.startedAt, entry.endedAt, dayStart), 0);
    const observedMs = samples.reduce((sum, entry) => sum + clippedDuration(entry.bucketStartMs, entry.bucketEndMs, dayStart), 0);
    const apps = new Map<string, number>();
    for (const sample of samples) apps.set(sample.appName || "Unknown app", (apps.get(sample.appName || "Unknown app") ?? 0) + clippedDuration(sample.bucketStartMs, sample.bucketEndMs, dayStart));
    const article = document.createElement("article"); article.className = "day-card";
    const date = new Date(dayStart); const isToday = startOfDay(now) === dayStart;
    const heading = document.createElement("header"); heading.innerHTML = `<div><span>${isToday ? "Today" : new Intl.DateTimeFormat(undefined, { weekday: "short" }).format(date)}</span><strong>${new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(date)}</strong></div><small>${confirmedMs ? formatDuration(confirmedMs, false) + " confirmed" : "No confirmed time"}</small>`;
    const confirmedLane = lane("Confirmed", confirmed.map((entry) => `${entry.description} · ${formatDuration(clippedDuration(entry.startedAt, entry.endedAt, dayStart), false)}`), "confirmed");
    const appLabels = [...apps.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4).map(([name, duration]) => `${name} · ${formatDuration(duration, false)}`);
    const observedLane = lane("Observed", appLabels, "observed", samples.length === 0 ? (nativeStatus.native ? "No Mac samples" : "Open the Mac app to collect local context") : `${formatDuration(observedMs, false)} device presence`);
    article.append(heading, confirmedLane, observedLane); return article;
  }));
}

function lane(label: string, entries: string[], kind: "confirmed" | "observed", empty = "Uncovered"): HTMLElement {
  const row = document.createElement("div"); row.className = `summary-lane ${kind}`;
  const title = document.createElement("span"); title.className = "lane-label"; title.textContent = label;
  const content = document.createElement("div"); content.className = "lane-content";
  if (!entries.length) { const gap = document.createElement("span"); gap.className = "lane-empty"; gap.textContent = empty; content.append(gap); }
  else for (const entry of entries) { const chip = document.createElement("span"); chip.textContent = entry; content.append(chip); }
  row.append(title, content); return row;
}

async function refreshNotifications(): Promise<void> {
  const state = await notificationState();
  const labels: Record<NotificationState["setup"], string> = { ready: "Ready", "install-ios": "Install first", unsupported: "Unavailable", denied: "Blocked", available: "Not enabled", unconfigured: "Server setup", error: "Needs attention" };
  element("notification-state").textContent = labels[state.setup]; element("notification-state").dataset.state = state.setup; element("notification-detail").textContent = state.detail;
  element("notification-enable").hidden = state.setup !== "available"; element("notification-test").hidden = state.setup !== "ready";
  const nudge = element<HTMLElement>("notification-nudge"); nudge.hidden = !["available", "install-ios", "denied"].includes(state.setup);
  element("notification-nudge-copy").textContent = state.setup === "install-ios" ? "Install Time Tile on this phone to receive check-ins." : state.setup === "denied" ? "Notifications are blocked on this device." : "Enable quiet check-ins for uncovered time.";
}

async function refreshNativeStatus(): Promise<void> {
  try { nativeStatus = await loadNativeStatus(); } catch (error) { nativeStatus = { native: true, collecting: false, inputMonitoring: "unknown", presence: "unknown", error: error instanceof Error ? error.message : "Collector unavailable" }; }
  const state = element<HTMLElement>("collector-state"); const detail = element<HTMLElement>("collector-detail"); const permission = element<HTMLButtonElement>("collector-permission"); const toggle = element<HTMLButtonElement>("collector-toggle"); const alerts = element<HTMLButtonElement>("native-notification-enable");
  if (!nativeStatus.native) { state.textContent = "Web only"; detail.textContent = "Install and open Time Tile for Mac to add private, local activity context."; permission.hidden = true; toggle.hidden = true; alerts.hidden = true; updatePresence({ state: "unknown" }); return; }
  state.textContent = nativeStatus.collecting ? "Collecting locally" : "Paused"; state.dataset.state = nativeStatus.collecting ? "ready" : "available";
  detail.textContent = nativeStatus.error ?? (nativeStatus.inputMonitoring === "granted" ? "App focus, idle time, and aggregate interaction counts stay in this Mac’s local vault." : "App focus and idle time are available. Allow Input Monitoring for aggregate interaction counts.");
  permission.hidden = nativeStatus.inputMonitoring === "granted"; toggle.hidden = false; toggle.textContent = nativeStatus.collecting ? "Pause" : "Resume";
  alerts.hidden = await nativeNotificationPermission(false) === "granted";
  updatePresence({ state: nativeStatus.presence, app: nativeStatus.appName });
}

function renderNudgeSettings(): void {
  element<HTMLSelectElement>("nudges-enabled").value = ledger.nudgePolicy.enabled ? "on" : "off";
  element<HTMLSelectElement>("review-after").value = String(ledger.nudgePolicy.reviewAfterMinutes);
  element<HTMLInputElement>("quiet-start").value = ledger.nudgePolicy.quietStart;
  element<HTMLInputElement>("quiet-end").value = ledger.nudgePolicy.quietEnd;
  element<HTMLSelectElement>("notification-privacy").value = ledger.nudgePolicy.includeTaskName ? "task" : "private";
  syncEnhancedControls(element("nudge-form"));
  element("nudge-status").textContent = ledger.nudgePolicy.enabled ? "Ambiguous activity stays quiet. Strong signals wait for a natural boundary." : "Smart check-ins are off on this device.";
}

function saveNudgeSettings(): void {
  ledger.nudgePolicy = {
    enabled: element<HTMLSelectElement>("nudges-enabled").value === "on",
    reviewAfterMinutes: Number(element<HTMLSelectElement>("review-after").value),
    quietStart: element<HTMLInputElement>("quiet-start").value || "22:00",
    quietEnd: element<HTMLInputElement>("quiet-end").value || "08:00",
    includeTaskName: element<HTMLSelectElement>("notification-privacy").value === "task",
  };
  saveLedger(); renderNudgeSettings(); updateRunning(); showToast("Check-in preferences saved.");
}

const modeLabels: Record<TagDefinition["defaultActivityMode"], string> = { mixed: "No device assumption", interactive_mac: "Interactive Mac", passive_mac: "Passive Mac", away_from_mac: "Usually away from Mac" };

function focusTagName(tagId: string): void {
  requestAnimationFrame(() => {
    [...document.querySelectorAll<HTMLButtonElement>(".tag-name-action")]
      .find((candidate) => candidate.dataset.tagId === tagId)?.focus();
  });
}

function beginTagNameEdit(button: HTMLButtonElement, tagId: string): void {
  const tag = ledger.tags.find((candidate) => candidate.id === tagId);
  if (!tag) throw new Error("This tag is no longer available.");
  beginInlineTextEdit({
    trigger: button,
    value: tag.label,
    label: `Rename tag: ${tag.label}`,
    inputClassName: "tag-name-input",
    maxLength: 40,
    onCommit: (value) => {
      const renamed = renameTag(tag, value);
      if (ledger.tags.some((candidate) => candidate.id !== tag.id && !candidate.archivedAt && candidate.label.toLowerCase() === renamed.label.toLowerCase())) {
        throw new Error("That tag already exists.");
      }
      if (renamed === tag) return;
      ledger.tags = ledger.tags.map((candidate) => candidate.id === tag.id ? renamed : candidate);
      saveLedger();
      renderTagSettings();
      renderNow();
      showToast("Tag renamed everywhere.");
    },
    onError: (error) => showToast(error instanceof Error ? error.message : "Could not rename this tag."),
    onAfterClose: () => focusTagName(tagId),
  });
}

function renderTagSettings(): void {
  const list = element("tag-list");
  list.replaceChildren(...activeTags().map((tag) => {
    const row = document.createElement("div"); row.className = "tag-row";
    const main = document.createElement("div"); main.className = "tag-row-main";
    const swatch = document.createElement("span"); swatch.className = "tag-swatch"; swatch.style.setProperty("--tag-color", tag.color);
    const name = document.createElement("button"); name.type = "button"; name.className = "tag-name-action"; name.dataset.tagId = tag.id; name.textContent = tag.label; name.setAttribute("aria-label", `Rename ${tag.label}`); name.addEventListener("click", () => beginTagNameEdit(name, tag.id)); main.append(swatch, name);
    const mode = document.createElement("small"); mode.textContent = modeLabels[tag.defaultActivityMode]; main.append(mode);
    const favorite = document.createElement("button"); favorite.type = "button"; favorite.className = "ui-button ui-button-quiet ui-button-small"; favorite.append(icon("pin"), document.createTextNode(tag.isFavorite ? "Unpin" : "Pin")); favorite.setAttribute("aria-label", `${tag.isFavorite ? "Unpin" : "Pin"} ${tag.label}`);
    favorite.addEventListener("click", () => { tag.isFavorite = !tag.isFavorite; saveLedger(); renderTagSettings(); renderNow(); });
    const archive = document.createElement("button"); archive.type = "button"; archive.className = "ui-button ui-button-danger ui-button-small archive-tag"; archive.append(icon("trash"), document.createTextNode("Archive")); archive.setAttribute("aria-label", `Archive ${tag.label}`);
    archive.addEventListener("click", () => { if (!confirm(`Archive “${tag.label}”? Existing entries will keep it.`)) return; ledger.tags = ledger.tags.map((candidate) => candidate.id === tag.id ? archiveTag(candidate) : candidate); saveLedger(); renderTagSettings(); renderNow(); showToast("Tag archived. History is unchanged."); });
    row.append(main, favorite, archive); return row;
  }));
  element("tag-status").textContent = `${activeTags().length} active tags. Archived tags remain attached to history.`;
}

function formatDate(timestamp: string | null): string { return timestamp ? new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(new Date(timestamp)) : "Never"; }

function renderPasskeys(passkeys: PasskeySummary[]): void {
  passkeyList.replaceChildren(...passkeys.map((passkey) => {
    const row = document.createElement("tr"); const identity = document.createElement("td"); const name = document.createElement("strong"); name.textContent = passkey.label; identity.append(name);
    if (passkey.current) { const current = document.createElement("span"); current.className = "current-badge"; current.textContent = "Current"; identity.append(current); }
    const detail = document.createElement("small"); detail.textContent = passkey.backedUp ? "Synced passkey" : "Device-bound passkey"; identity.append(detail);
    const created = document.createElement("td"); created.textContent = formatDate(passkey.createdAt); const used = document.createElement("td"); used.textContent = formatDate(passkey.lastUsedAt); const action = document.createElement("td");
    const remove = document.createElement("button"); remove.type = "button"; remove.className = "ui-button ui-button-danger ui-button-small remove-passkey"; remove.append(icon("trash"), document.createTextNode("Remove")); remove.disabled = passkey.current || passkeys.length === 1; remove.title = passkey.current ? "Sign in with another passkey before removing this one." : passkeys.length === 1 ? "The final passkey cannot be removed." : `Remove ${passkey.label}`; remove.setAttribute("aria-label", `Remove ${passkey.label}`);
    remove.addEventListener("click", async () => { if (!confirm(`Remove “${passkey.label}” from Time Tile?`)) return; remove.disabled = true; passkeyStatus.textContent = `Removing ${passkey.label}…`; try { await removePasskey(passkey.id); await refreshPasskeys(); showToast("Passkey removed."); } catch (error) { passkeyStatus.textContent = error instanceof Error ? error.message : "Could not remove passkey."; remove.disabled = false; } });
    action.append(remove); row.append(identity, created, used, action); return row;
  }));
  passkeyStatus.textContent = passkeys.length === 1 ? "Add a second passkey before removing this one." : `${passkeys.length} passkeys registered.`;
}

async function refreshPasskeys(): Promise<void> { try { renderPasskeys(await listPasskeys()); } catch (error) { passkeyStatus.textContent = error instanceof Error ? error.message : "Could not load passkeys."; } }

form.addEventListener("submit", (event) => { event.preventDefault(); try { begin(input.value, [...startTagIds]); input.value = ""; } catch (error) { showToast(error instanceof Error ? error.message : "Could not start."); } });
switchForm.addEventListener("submit", (event) => { event.preventDefault(); try { submitSwitch(switchInput.value); } catch (error) { showToast(error instanceof Error ? error.message : "Could not switch."); } });
element("finish-button").addEventListener("click", () => complete());
element("switch-button").addEventListener("click", openSwitch);
element("switch-cancel").addEventListener("click", closeSwitch);
element("review-switch-button").addEventListener("click", openSwitch);
element("review-finish-button").addEventListener("click", () => complete());
element("continue-button").addEventListener("click", () => { if (!ledger.running) return; resolvePrompt("continued"); ledger.running.lastReviewedAt = Date.now(); delete ledger.running.snoozedUntil; presentedPromptKey = null; saveLedger(); reviewCard.hidden = true; runningSummary.hidden = false; renderNow(); showToast("Kept running. I’ll check later."); });
element("snooze-button").addEventListener("click", () => { if (!ledger.running) return; resolvePrompt("snoozed"); ledger.running.snoozedUntil = Date.now() + 30 * 60_000; presentedPromptKey = null; saveLedger(); reviewCard.hidden = true; runningSummary.hidden = false; renderNow(); showToast("Quiet for 30 minutes."); });
document.addEventListener("keydown", (event) => { if (event.key === "Escape" && !switchPanel.hidden) closeSwitch(); });

element<HTMLFormElement>("tag-form").addEventListener("submit", (event) => { event.preventDefault(); try { const label = element<HTMLInputElement>("tag-label"); const tag = createTag(label.value, element<HTMLInputElement>("tag-color").value, element<HTMLSelectElement>("tag-mode").value as TagDefinition["defaultActivityMode"]); if (ledger.tags.some((candidate) => !candidate.archivedAt && candidate.label.toLowerCase() === tag.label.toLowerCase())) throw new Error("That tag already exists."); ledger.tags.push(tag); label.value = ""; saveLedger(); renderTagSettings(); renderNow(); showToast("Tag created."); label.focus(); } catch (error) { element("tag-status").textContent = error instanceof Error ? error.message : "Could not create tag."; } });
element<HTMLFormElement>("nudge-form").addEventListener("change", saveNudgeSettings);

passkeyForm.addEventListener("submit", async (event) => { event.preventDefault(); const label = passkeyLabel.value.trim(); if (!label) return; passkeyAddButton.disabled = true; passkeyStatus.textContent = "Waiting for your device…"; try { await addPasskey(label); passkeyLabel.value = ""; await refreshPasskeys(); showToast("Passkey added."); } catch (error) { passkeyStatus.textContent = passkeyError(error, "enroll"); } finally { passkeyAddButton.disabled = false; passkeyLabel.focus(); } });

document.querySelectorAll<HTMLAnchorElement>("[data-route]").forEach((link) => link.addEventListener("click", (event) => { event.preventDefault(); void showPage(link.dataset.route as Page, true); }));
document.querySelectorAll<HTMLAnchorElement>("[data-go-now]").forEach((link) => link.addEventListener("click", (event) => { event.preventDefault(); void showPage("now", true); }));
document.querySelectorAll<HTMLButtonElement>("[data-go-profile]").forEach((button) => button.addEventListener("click", () => void showPage("profile", true)));
document.querySelectorAll<HTMLButtonElement>("[data-theme-choice]").forEach((button) => button.addEventListener("click", () => applyTheme(button.dataset.themeChoice as Theme, true)));
document.querySelectorAll<HTMLButtonElement>(".theme-toggle").forEach((button) => button.addEventListener("click", () => applyTheme(activeTheme === "light" ? "dark" : "light", true)));
window.addEventListener("popstate", () => void showPage(routeForPath()));
window.addEventListener("timetile-native-presence", ((event: CustomEvent<PresenceDetail>) => updatePresence(event.detail)) as EventListener);
document.addEventListener("visibilitychange", () => { if (!document.hidden) { renderNow(); void refreshNativeStatus(); void syncClient?.refresh(); } else updateRunning(); });
window.addEventListener("focus", () => void syncClient?.refresh());
window.addEventListener("online", () => void syncClient?.refresh());

element("notification-enable").addEventListener("click", async () => { const button = element<HTMLButtonElement>("notification-enable"); button.disabled = true; try { await enableNotifications(); await refreshNotifications(); showToast("Notifications are on."); } catch (error) { showToast(error instanceof Error ? error.message : "Could not enable notifications."); } finally { button.disabled = false; } });
element("notification-test").addEventListener("click", async () => { try { await sendTestNotification(); showToast("Test sent."); } catch (error) { showToast(error instanceof Error ? error.message : "Test failed."); } });
element("native-notification-enable").addEventListener("click", async () => { const permission = await nativeNotificationPermission(true); showToast(permission === "granted" ? "Mac alerts are on." : "Mac alerts were not allowed."); await refreshNativeStatus(); });
element("collector-permission").addEventListener("click", async () => { nativeStatus = await requestInputMonitoring(); await refreshNativeStatus(); });
element("collector-toggle").addEventListener("click", async () => { nativeStatus = await setCollectionEnabled(!nativeStatus.collecting); await refreshNativeStatus(); });

async function lockApplication(): Promise<void> { try { await logout(); } finally { syncClient?.stop(); syncClient = undefined; window.clearInterval(syncTick); appShell.hidden = true; bottomNav.hidden = true; authView.hidden = false; await initializeAuth(); } }
element("logout-button").addEventListener("click", () => void lockApplication());
element("profile-logout-button").addEventListener("click", () => void lockApplication());
if ("serviceWorker" in navigator) navigator.serviceWorker.register("/sw.js").catch(() => undefined);

async function unlock(): Promise<void> {
  ledger = loadLedger(); authView.hidden = true; appShell.hidden = false; bottomNav.hidden = false; renderNow(); await showPage(routeForPath());
  syncClient?.stop();
  syncClient = new LedgerSyncClient(ledger, localStorage, new HttpSyncTransport(), applySyncedLedger, renderSyncStatus);
  await Promise.all([syncClient.initialize(), refreshNotifications(), refreshNativeStatus()]);
  window.clearInterval(syncTick); syncTick = window.setInterval(() => { if (!document.hidden) void syncClient?.refresh(); }, 15_000);
  window.clearInterval(nativeTick); nativeTick = window.setInterval(() => void refreshNativeStatus(), 15_000); if (routeForPath() === "now") input.focus();
}

function showAuthAction(
  label: string,
  handler: () => Promise<void>,
  after: () => Promise<void> = unlock,
): void {
  authButton.textContent = label; authButton.hidden = false; authButton.disabled = false;
  authButton.onclick = async () => { authButton.disabled = true; element("auth-status").textContent = "Waiting for your device…"; try { await handler(); await after(); } catch (error) { element("auth-status").textContent = passkeyError(error, enrollmentToken ? "enroll" : "authenticate"); authButton.disabled = false; authButton.focus(); } };
}

async function finishDeviceApproval(): Promise<void> {
  authButton.hidden = true;
  element("auth-eyebrow").textContent = "Mac approved";
  element("auth-title").textContent = "You’re all set.";
  element("auth-copy").textContent = "Time Tile is unlocking on your Mac. You can close this tab.";
  element("auth-status").textContent = "This approval link cannot be used again.";
}

async function waitForBrowserApproval(): Promise<void> {
  const authorization = await startDeviceAuthorization();
  await openDeviceAuthorization(authorization.requestId);
  let cancelled = false;
  authCancelButton.hidden = false;
  authCancelButton.onclick = () => {
    cancelled = true;
    authCancelButton.disabled = true;
    element("auth-status").textContent = "Cancelling browser approval…";
  };
  element("auth-status").textContent = "Approve this Mac in the browser window we just opened.";
  const expiresAt = Date.parse(authorization.expiresAt);
  let pollDelayMs = 1_000;
  try {
    while (!cancelled && Date.now() < expiresAt) {
      await new Promise((resolve) => window.setTimeout(resolve, pollDelayMs));
      pollDelayMs = Math.min(pollDelayMs + 500, 5_000);
      if (cancelled) break;
      try {
      const status = await completeDeviceAuthorization(
          authorization.verifier,
        );
        if (status === "complete") return;
        if (status === "unavailable") break;
      } catch {
        element("auth-status").textContent = "Still waiting for browser approval…";
      }
    }
  } finally {
    authCancelButton.hidden = true;
    authCancelButton.disabled = false;
  }
  if (cancelled) throw new Error("Browser approval cancelled. Start again when you’re ready.");
  throw new Error("This Mac approval expired. Start again to open a fresh browser link.");
}

async function initializeAuth(): Promise<void> {
  authView.hidden = false; authButton.hidden = true; authCancelButton.hidden = true; authLabelWrap.hidden = true; element("auth-status").textContent = ""; element("auth-eyebrow").textContent = "Private by design"; element("auth-title").textContent = "Unlock your time."; element("auth-copy").textContent = "Checking Time Tile’s lock…";
  try {
    const state = await loadAuthState();
    if (deviceApprovalPage) {
      if (!deviceApprovalRequestId || !/^[a-f0-9]{64}$/.test(deviceApprovalRequestId)) {
        element("auth-eyebrow").textContent = "Link unavailable";
        element("auth-title").textContent = "Start from your Mac.";
        element("auth-copy").textContent = "This approval link is incomplete. Open Time Tile on your Mac and choose Continue in browser again.";
        return;
      }
      if (state.authenticated) {
        element("auth-eyebrow").textContent = "Mac sign-in";
        element("auth-title").textContent = "Approve this Mac?";
        element("auth-copy").textContent = "This creates a separate Time Tile session for the Mac app. Your passkey and this browser session stay here.";
        showAuthAction(
          "Approve this Mac",
          () => approveDeviceAuthorization(deviceApprovalRequestId),
          finishDeviceApproval,
        );
        return;
      }
      if (!passkeysSupported()) {
        element("auth-title").textContent = "A passkey browser is required.";
        element("auth-copy").textContent = "Open this link in Safari, Chrome, or another browser that can use your passkey.";
        return;
      }
      if (state.enrolled) {
        element("auth-eyebrow").textContent = "Mac sign-in";
        element("auth-title").textContent = "Unlock to approve.";
        element("auth-copy").textContent = "Use your existing passkey here. You’ll approve the Mac on the next screen.";
        showAuthAction("Unlock to approve", authenticatePasskey, initializeAuth);
        return;
      }
      element("auth-title").textContent = "Time Tile is not set up yet.";
      element("auth-copy").textContent = "Create the first passkey before approving another device.";
      return;
    }
    if (state.authenticated) { await unlock(); return; }
    if (state.enrolled && isNative()) {
      element("auth-eyebrow").textContent = "Secure browser handoff";
      element("auth-title").textContent = "Unlock in your browser.";
      element("auth-copy").textContent = "Use iCloud Keychain or 1Password in your usual browser, then approve this Mac. Your passkey stays in the browser.";
      showAuthAction("Continue in browser", waitForBrowserApproval);
      return;
    }
    if (!passkeysSupported()) { element("auth-title").textContent = "A secure browser is required."; element("auth-copy").textContent = "Open Time Tile at its HTTPS address in Safari, Chrome, or another passkey-capable browser."; return; }
    if (!state.enrolled && enrollmentToken) { element("auth-eyebrow").textContent = "One-time setup"; element("auth-title").textContent = "Make Time Tile yours."; element("auth-copy").textContent = "Create your first key to this private space. You can add another from Profile once inside."; authLabelWrap.hidden = false; showAuthAction("Create my passkey", async () => { const label = authLabelInput.value.trim(); if (!label) { authLabelInput.focus(); throw new Error("Name this passkey first—for example, iCloud Keychain."); } await enrollPasskey(enrollmentToken, label); }); return; }
    if (state.enrolled) { element("auth-copy").textContent = "No password. No tracking. Just your passkey."; showAuthAction("Unlock with passkey", authenticatePasskey); return; }
    element("auth-eyebrow").textContent = "Setup required"; element("auth-title").textContent = "Time Tile is sealed."; element("auth-copy").textContent = "Open the unexpired one-time setup link to create its first passkey.";
  } catch (error) { element("auth-title").textContent = "Time Tile is out of reach."; element("auth-copy").textContent = error instanceof Error ? error.message : "Try again in a moment."; showAuthAction("Try again", initializeAuth); }
}

hydrateIcons();
enhanceControls();
document.documentElement.dataset.native = isNative() ? "true" : "false";
initializeTheme();
void initializeAuth();
